// frontend/src/pages/AssessmentTakingPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import authAxios from '../auth/authAxios';
import QuestionComponent from '../components/QuestionComponent';

// Dynamic loading messages that cycle every 2 seconds
const LOADING_MESSAGES = [
  "Creating a dynamic assessment tailored to your role...",
  "Analyzing your profile and experience level...",
  "Generating questions specific to your domain...",
  "Customizing difficulty to match your expertise...",
  "Building assessment phases for your skill set...",
  "Verifying questions align with your role requirements...",
  "Preparing real-world scenarios for your field...",
  "Optimizing challenge complexity for your level...",
  "Crafting personalized technical problems...",
  "Finalizing your custom assessment experience...",
  "Almost ready - putting the finishing touches..."
];

// Dynamic submission messages that cycle every 2 seconds
const SUBMISSION_MESSAGES = [
  "Reviewing your responses for key strengths...",
  "Evaluating problem-solving and decision-making...",
  "Assessing alignment with role expectations...",
  "Analyzing performance across skill domains...",
  "Generating insights from your assessment journey...",
  "Scoring and summarizing your results...",
  "Calibrating your proficiency across core competencies...",
  "Synthesizing personalized feedback for growth...",
  "Comparing outcomes to industry benchmarks...",
  "Finalizing a comprehensive performance summary..."
];

interface Question {
  question_id: string;
  question_type: string;
  question_text: string;
  code_snippet?: string;
  options?: string;
  points: number;
  order_index: number;
  skill_focus?: string;
}

interface AssessmentData {
  attempt_id: string;
  questions: Question[];
  time_remaining_minutes: number;
}

interface StartAssessmentResponse {
  attempt_id: string;
  assessment: any;
  time_limit_minutes: number;
  started_at: string;
  current_question_index: number;
  is_resuming: boolean;
  total_questions: number;
}

interface UserResponse {
  question_id: string;
  user_answer: string;
  response_time_seconds: number;
}

const AssessmentTakingPage: React.FC = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();
  
  const [assessmentData, setAssessmentData] = useState<AssessmentData | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<Map<string, UserResponse>>(new Map());
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [submissionMessage, setSubmissionMessage] = useState('');
  const [submissionMessageIndex, setSubmissionMessageIndex] = useState(0);
  const [isResumedAssessment, setIsResumedAssessment] = useState(false);

  useEffect(() => {
    if (assessmentId) {
      startAssessment();
    }
  }, [assessmentId]);

  // Cycle through loading messages while loading
  useEffect(() => {
    if (loading) {
      // Set initial message
      setLoadingMessage(LOADING_MESSAGES[0]);
      setLoadingMessageIndex(0);

      const interval = setInterval(() => {
        setLoadingMessageIndex((prevIndex) => {
          // Only advance if we haven't reached the end of the array
          if (prevIndex < LOADING_MESSAGES.length - 1) {
            const nextIndex = prevIndex + 1;
            setLoadingMessage(LOADING_MESSAGES[nextIndex]);
            return nextIndex;
          }
          // Stay at the last message if we've reached the end
          return prevIndex;
        });
      }, 2000); // Change message every 2 seconds

      return () => clearInterval(interval);
    }
  }, [loading]);

  // Cycle through submission messages while submitting
  useEffect(() => {
    if (submitting) {
      // Set initial message
      setSubmissionMessage(SUBMISSION_MESSAGES[0]);
      setSubmissionMessageIndex(0);

      const interval = setInterval(() => {
        setSubmissionMessageIndex((prevIndex) => {
          // Only advance if we haven't reached the end of the array
          if (prevIndex < SUBMISSION_MESSAGES.length - 1) {
            const nextIndex = prevIndex + 1;
            setSubmissionMessage(SUBMISSION_MESSAGES[nextIndex]);
            return nextIndex;
          }
          // Stay at the last message if we've reached the end
          return prevIndex;
        });
      }, 2000); // Change message every 2 seconds

      return () => clearInterval(interval);
    }
  }, [submitting]);

  useEffect(() => {
    if (assessmentData) {
      setTimeRemaining(assessmentData.time_remaining_minutes * 60);
    }
  }, [assessmentData]);

  useEffect(() => {
    if (timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [timeRemaining]);

  const startAssessment = async () => {
    try {
      setLoading(true);

      // Start the assessment
      const startResponse = await authAxios.post(`/api/assessments/${assessmentId}/start`);
      const startData: StartAssessmentResponse = startResponse.data;

      // Get questions
      const questionsResponse = await authAxios.get(`/api/assessments/${assessmentId}/questions`);

      setAssessmentData(questionsResponse.data);

      // Handle resume functionality
      if (startData.is_resuming && startData.current_question_index > 0) {
        // Ensure question index is within bounds - if out of bounds, show last question
        const maxQuestionIndex = questionsResponse.data.questions.length - 1;
        const safeQuestionIndex = Math.min(startData.current_question_index, maxQuestionIndex);

        if (safeQuestionIndex !== startData.current_question_index) {
          console.warn(`Question index ${startData.current_question_index} out of bounds (max: ${maxQuestionIndex}). Using ${safeQuestionIndex} instead.`);
        }

        // Resume from the safe question index
        setCurrentQuestionIndex(safeQuestionIndex);
        setIsResumedAssessment(true);
        console.log(`Resuming assessment at question ${safeQuestionIndex + 1} of ${questionsResponse.data.questions.length}`);

        // Load existing responses for resumed assessment
        try {
          const responsesResponse = await authAxios.get(`/api/assessments/${assessmentId}/responses`);
          const existingResponses = responsesResponse.data.responses;

          // Convert to Map format expected by the component
          const responseMap = new Map();
          Object.entries(existingResponses).forEach(([questionId, responseData]: [string, any]) => {
            responseMap.set(questionId, responseData);
          });

          setResponses(responseMap);
          console.log(`Loaded ${responseMap.size} existing responses for resumed assessment`);
        } catch (err) {
          console.error('Failed to load existing responses:', err);
          // Don't fail the resume if we can't load responses - user can still continue
        }
      } else {
        // Starting fresh
        setCurrentQuestionIndex(0);
        setIsResumedAssessment(false);
      }

      setQuestionStartTime(Date.now());
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to start assessment');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId: string, answer: string) => {
    const responseTime = Math.floor((Date.now() - questionStartTime) / 1000);

    setResponses(prev => {
      const newResponses = new Map(prev);
      newResponses.set(questionId, {
        question_id: questionId,
        user_answer: answer,
        response_time_seconds: responseTime
      });
      return newResponses;
    });
  };

  const submitCurrentResponse = async () => {
    if (!assessmentData) return;

    const currentQuestion = assessmentData.questions[currentQuestionIndex];
    const response = responses.get(currentQuestion.question_id);

    if (response) {
      try {
        await authAxios.post(
          `/api/assessments/${assessmentId}/questions/${currentQuestion.question_id}/respond`,
          response
        );
      } catch (err: any) {
        console.error('Failed to submit response:', err);
        // Re-throw the error so calling functions can handle it
        throw new Error(err.response?.data?.detail || 'Failed to save your response. Please try again.');
      }
    }
  };

  const handleNext = async () => {
    if (navigating) return; // Prevent multiple clicks

    try {
      setNavigating(true);
      setError(''); // Clear any previous errors

      // Try to submit current response - this will throw if it fails
      await submitCurrentResponse();

      if (currentQuestionIndex < assessmentData!.questions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
        setQuestionStartTime(Date.now());
      } else {
        await handleSubmitAssessment();
      }
    } catch (err: any) {
      // Show the error message and don't navigate
      setError(err.message || err.response?.data?.detail || 'Failed to save your response. Please try again.');
    } finally {
      setNavigating(false);
    }
  };

  const handlePrevious = async () => {
    if (navigating) return; // Prevent multiple clicks

    try {
      setNavigating(true);
      setError(''); // Clear any previous errors

      // Try to submit current response - this will throw if it fails
      await submitCurrentResponse();

      if (currentQuestionIndex > 0) {
        setCurrentQuestionIndex(prev => prev - 1);
        setQuestionStartTime(Date.now());
      }
    } catch (err: any) {
      // Show the error message and don't navigate
      setError(err.message || err.response?.data?.detail || 'Failed to save your response. Please try again.');
    } finally {
      setNavigating(false);
    }
  };

  const handleSubmitAssessment = async () => {
    if (!assessmentData) return;

    try {
      setSubmitting(true);
      setError(''); // Clear any previous errors

      // Submit any remaining responses - this will throw if it fails
      await submitCurrentResponse();

      // Complete the assessment
      await authAxios.post(`/api/assessments/attempts/${assessmentData.attempt_id}/complete`);

      // Navigate to completion page (users don't see results anymore)
      navigate(`/assessments/${assessmentId}/completed`);
    } catch (err: any) {
      // Show the error message and don't submit
      setError(err.message || err.response?.data?.detail || 'Failed to submit assessment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTimeUp = async () => {
    await handleSubmitAssessment();
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = () => {
    if (!assessmentData) return 0;
    return ((currentQuestionIndex + 1) / assessmentData.questions.length) * 100;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col justify-center items-center px-4">
        <div className="text-center max-w-md animate-fade-in">
          {/* Spinning circle */}
          <div className="flex justify-center mb-6">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
          </div>

          {/* Dynamic status message */}
          <div className="mb-4">
            <h2 className="text-2xl font-semibold text-white mb-2">Preparing Your Assessment</h2>
            <div className="h-16 flex items-center justify-center">
              <p className="text-gray-300 text-lg transition-all duration-700 ease-in-out transform">
                {loadingMessage}
              </p>
            </div>
          </div>

          {/* Progress dots indicator */}
          <div className="flex justify-center space-x-2 mt-6">
            {LOADING_MESSAGES.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                  index === loadingMessageIndex ? 'bg-blue-500' : 'bg-gray-600'
                }`}
              />
            ))}
          </div>

          {/* Subtle hint about what's happening */}
          <div className="mt-8 text-sm text-gray-400">
            <p>This may take a few moments as we create questions specifically for your role and experience level.</p>
          </div>
        </div>
      </div>
    );
  }

  if (submitting) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col justify-center items-center px-4">
        <div className="text-center max-w-md animate-fade-in">
          {/* Spinning circle */}
          <div className="flex justify-center mb-6">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500"></div>
          </div>

          {/* Dynamic status message */}
          <div className="mb-4">
            <h2 className="text-2xl font-semibold text-white mb-2">Processing Your Assessment</h2>
            <div className="h-16 flex items-center justify-center">
              <p className="text-gray-300 text-lg transition-all duration-700 ease-in-out transform">
                {submissionMessage}
              </p>
            </div>
          </div>

          {/* Progress dots indicator */}
          <div className="flex justify-center space-x-2 mt-6">
            {SUBMISSION_MESSAGES.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                  index === submissionMessageIndex ? 'bg-green-500' : 'bg-gray-600'
                }`}
              />
            ))}
          </div>

          {/* Subtle hint about what's happening */}
          <div className="mt-8 text-sm text-gray-400">
            <p>We're analyzing your responses and generating personalized feedback. This may take up to 60 seconds.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    // If we're in an assessment and it's a submission error, show retry option
    const isSubmissionError = error.includes('save your response') || error.includes('Failed to submit');
    const inAssessment = assessmentData && !loading;

    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                {isSubmissionError ? 'Response Not Saved' : 'Error'}
              </h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
              <div className="mt-4 space-x-3">
                {inAssessment && isSubmissionError && (
                  <button
                    onClick={() => setError('')}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm"
                  >
                    Try Again
                  </button>
                )}
                <button
                  onClick={() => navigate('/assessments')}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm"
                >
                  Back to Assessments
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!assessmentData) {
    return <div>No assessment data available</div>;
  }

  // Safety checks

  if (!assessmentData.questions || assessmentData.questions.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">No questions available for this assessment.</div>
        </div>
      </div>
    );
  }

  if (currentQuestionIndex >= assessmentData.questions.length) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="text-yellow-800">Question index out of bounds.</div>
        </div>
      </div>
    );
  }

  const currentQuestion = assessmentData.questions[currentQuestionIndex];

  if (!currentQuestion) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">Current question is undefined.</div>
        </div>
      </div>
    );
  }

  const currentResponse = responses.get(currentQuestion.question_id);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Header with timer and progress */}
      <div className="bg-gray-800 rounded-lg p-4 mb-6">
        {/* Resume indicator */}
        {isResumedAssessment && (
          <div className="mb-3 flex items-center space-x-2 text-yellow-400 text-sm">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span>Assessment resumed - continuing from where you left off</span>
          </div>
        )}

        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="text-white">
              <span className="text-sm text-gray-400">Question </span>
              <span className="font-semibold">
                {currentQuestionIndex + 1} of {assessmentData.questions.length}
              </span>
            </div>
            <div className="w-64 bg-gray-700 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${getProgressPercentage()}%` }}
              ></div>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className={`text-lg font-mono ${timeRemaining < 300 ? 'text-red-400' : 'text-white'}`}>
              ⏱️ {formatTime(timeRemaining)}
            </div>
          </div>
        </div>
      </div>

      {/* Question */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6 relative">
        {/* Loading overlay when navigating */}
        {navigating && (
          <div className="absolute inset-0 bg-gray-900 bg-opacity-50 rounded-lg flex items-center justify-center z-10">
            <div className="text-white text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
              <p className="text-sm">Saving your response...</p>
            </div>
          </div>
        )}

        <QuestionComponent
          question={currentQuestion}
          value={currentResponse?.user_answer || ''}
          onChange={(answer) => handleAnswerChange(currentQuestion.question_id, answer)}
          disabled={navigating || submitting}
        />
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0 || navigating || submitting}
          className="px-6 py-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white rounded-md transition-colors duration-200 flex items-center"
        >
          {navigating && currentQuestionIndex > 0 ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Loading...
            </>
          ) : (
            'Previous'
          )}
        </button>

        <div className="flex space-x-3">
          {/* Pause button */}
          <button
            onClick={async () => {
              try {
                // Save current response before pausing
                await submitCurrentResponse();

                // Mark assessment as paused
                await authAxios.post(`/api/assessments/${assessmentId}/pause`);

                navigate('/assessments');
              } catch (err: any) {
                console.error('Failed to pause assessment:', err);

                // Show error message to user
                const errorMessage = err.response?.data?.detail || err.message || 'Failed to pause assessment';
                setError(`Failed to pause: ${errorMessage}. Please try again.`);

                // Don't navigate if there was an error - let user try again
              }
            }}
            disabled={navigating || submitting}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white rounded-md transition-colors duration-200 flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Pause
          </button>

          {currentQuestionIndex === assessmentData.questions.length - 1 ? (
            <button
              onClick={handleSubmitAssessment}
              disabled={submitting || navigating}
              className="px-8 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-500 disabled:cursor-not-allowed text-white rounded-md transition-colors duration-200 flex items-center"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                'Submit Assessment'
              )}
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={navigating || submitting}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-500 disabled:cursor-not-allowed text-white rounded-md transition-colors duration-200 flex items-center"
            >
              {navigating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Loading...
                </>
              ) : (
                'Next'
              )}
            </button>
          )}
        </div>
      </div>

      {/* Warning for low time */}
      {timeRemaining < 300 && timeRemaining > 0 && (
        <div className="fixed bottom-4 right-4 bg-red-600 text-white p-4 rounded-lg shadow-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">Less than 5 minutes remaining!</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentTakingPage;
